import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Badge,
    Modal, Nav, NavItem, NavLink, TabContent, TabPane, Row, Col, InputGroup, Input, Table
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Edit, Plus, Trash2, Eye, User, FileText, CheckCircle, XCircle, Lock, Unlock, MapPin } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";
import StaticMapDisplay from "../../Components/Common/StaticMapDisplay";

interface BystanderData {
    id: number;
    name: string;
    gender: string;
    dob: string;
    phone_number: string;
    email: string | null;
    address: string;
    aadhaar_number: string;
    profile_pic: string | null;
    kyc_document: string;
    police_verification: string;
    education: string;
    experience_years: string;
    status: string;
    is_verified: boolean;
    is_enable: boolean;
    is_online: number;
    homelat?: number;
    homelng?: number;
    current_lat?: number;
    current_lng?: number;
    created_at: string;
}

export default function ViewBystanders() {
    const [data, setData] = useState<BystanderData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("id");
    const [sortDir, setSortDir] = useState("desc");
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [viewModal, setViewModal] = useState(false);
    const [selectedBystander, setSelectedBystander] = useState<BystanderData | null>(null);
    const [activeTab, setActiveTab] = useState('1');
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [isDarkMode, setIsDarkMode] = useState(document.body.classList.contains('dark-only'));

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedStatus]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchData(currentPage, perPage, sortBy, sortDir, searchTerm, selectedStatus);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, selectedStatus, refresh, currentPage, perPage, sortBy, sortDir]);

    const fetchData = async (page: number, size: number, sort: string, direction: string, search: string, isOnline: string) => {
        setLoading(true);
        try {
            const response: any = await apiPost(`bystander/get_all?page=${page}`, {
                per_page: size,
                sort_by: sort,
                sort_direction: direction,
                search: search,
                is_online: isOnline
            });
            setData(response.data || []);
            setTotalRows(response.total || 0);
        } catch (error) {
            toast.error("Failed to load bystanders");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || column.selector.toString().split('.').pop());
        setSortDir(sortDirection);
    };

    const handleAdd = () => navigate(`${process.env.PUBLIC_URL}/add_bystander`);
    const handleEdit = (id: number) => navigate(`${process.env.PUBLIC_URL}/edit_bystander`, { state: { id } });

    const handleView = (bystander: BystanderData) => {
        setSelectedBystander(bystander);
        setActiveTab('1');
        setViewModal(true);
    };

    const handleStatusUpdate = async (id: number, status: string) => {
        Swal.fire({
            title: `Confirm ${status}?`,
            text: `Are you sure you want to mark this bystander as ${status}?`,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: `Yes, ${status}`
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`bystander/updateStatus`, { id, status });
                    toast.success(`Bystander ${status} successfully`);
                    setRefresh(prev => prev + 1);
                    if (viewModal && selectedBystander?.id === id) {
                        setViewModal(false);
                    }
                } catch (error) {
                    toast.error(`Failed to update status`);
                }
            }
        });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`bystander/delete`, { id });
                    toast.success("Bystander deleted successfully");
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete bystander");
                }
            }
        });
    };

    const handleToggleEnable = async (id: number, currentStatus: boolean) => {
        const action = currentStatus ? 'Disable' : 'Enable';
        Swal.fire({
            title: `${action} Bystander?`,
            text: `Are you sure you want to ${action.toLowerCase()} this bystander?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: currentStatus ? '#d33' : '#3085d6',
            confirmButtonText: `Yes, ${action} it!`
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`bystander/toggleEnable`, { id });
                    toast.success(`Bystander ${action.toLowerCase()}d successfully`);
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error(`Failed to ${action.toLowerCase()} bystander`);
                }
            }
        });
    };

    const columns: any[] = [
        {
            name: <div><span className="fw-bold">Sl.No</span></div>,
            selector: (row: BystanderData, index: number) => (currentPage - 1) * perPage + index + 1,
            width: "70px"
        },
        { name: <div><span className="fw-bold">Name</span></div>, selector: (row: BystanderData) => row.name, sortable: true, sortField: "name", wrap: true },

        { name: <div><span className="fw-bold">Email</span></div>, selector: (row: BystanderData) => row.email, sortable: true, sortField: "email", wrap: true },
        { name: <div><span className="fw-bold">Phone</span></div>, selector: (row: BystanderData) => row.phone_number, sortable: true, sortField: "phone_number" },
        // { name: <div><span className="fw-bold">Aadhaar</span></div>, selector: (row: BystanderData) => row.aadhaar_number, sortable: true, sortField: "aadhaar_number" },
        { name: <div><span className="fw-bold">Gender</span></div>, selector: (row: BystanderData) => row.gender, sortable: true, sortField: "gender" },
        {
            name: <div><span className="fw-bold">Status</span></div>,
            sortable: true,
            sortField: "status",
            cell: (row: BystanderData) => (
                <Badge color={row.status === 'Approved' ? 'success' : row.status === 'Reject' ? 'danger' : 'warning'}>{row.status}</Badge>
            ),
        },
        {
            name: <div><span className="fw-bold">Availability</span></div>,
            sortable: true,
            sortField: "is_online",
            cell: (row: BystanderData) => (
                <Badge color={row.is_online === 1 ? 'success' : row.is_online === 2 ? 'info' : 'secondary'}>
                    {row.is_online === 1 ? 'Online' : row.is_online === 2 ? 'On Duty' : 'Offline'}
                </Badge>
            ),
        },
        {
            name: <div><span className="fw-bold">Actions</span></div>,
            width: "250px",
            cell: (row: BystanderData) => (
                <div className="d-flex gap-2">
                    <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Detail">
                        <Eye size={14} />
                    </Button>
                    {hasPermission('bystander-management.enable') && (
                        <Button
                            size="sm"
                            color={row.is_enable ? "warning" : "success"}
                            onClick={() => handleToggleEnable(row.id, row.is_enable)}
                            title={row.is_enable ? "Disable" : "Enable"}
                        >
                            {row.is_enable ? <Lock size={14} /> : <Unlock size={14} />}
                        </Button>
                    )}
                    {hasPermission('bystander-management.approve') && (
                        <>
                            {row.status !== 'Approved' && (
                                <Button size="sm" color="success" onClick={() => handleStatusUpdate(row.id, 'Approved')} title="Approve">
                                    <CheckCircle size={14} />
                                </Button>
                            )}
                            {row.status !== 'Reject' && (
                                <Button size="sm" color="warning" onClick={() => handleStatusUpdate(row.id, 'Reject')} title="Reject">
                                    <XCircle size={14} />
                                </Button>
                            )}
                        </>
                    )}
                    {hasPermission('bystander-management.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit"><Edit size={14} /></Button>
                    )}
                    {hasPermission('bystander-management.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete"><Trash2 size={14} /></Button>
                    )}
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };

    const isPdf = (filename: string) => filename.toLowerCase().endsWith('.pdf');

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Bystander Management" parent="General" title="Bystanders" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <H3 className="text-primary mb-0">Bystander Directory</H3>
                            {hasPermission('bystander-management.add') && (
                                <Btn color="primary" onClick={handleAdd} className="d-flex align-items-center gap-1">
                                    <Plus size={16} /> Add Bystander
                                </Btn>
                            )}
                        </div>

                        <Row className="mb-4 align-items-center g-0 border-bottom">
                            <Col md={8}>
                                <div className="d-flex custom-tabs">
                                    {[
                                        { id: '', label: 'All Bystanders' },
                                        { id: '1', label: 'Online' },
                                        { id: '2', label: 'On Duty' },
                                        { id: '0', label: 'Offline' },
                                    ].map((status) => (
                                        <div
                                            key={status.id}
                                            className={`tab-item ${selectedStatus === status.id ? 'active-tab' : ''}`}
                                            onClick={() => setSelectedStatus(status.id)}
                                            style={{
                                                padding: '10px 20px',
                                                cursor: 'pointer',
                                                borderBottom: selectedStatus === status.id ? '3px solid #00695c' : 'none',
                                                color: selectedStatus === status.id ? '#00695c' : '#64748b',
                                                fontWeight: selectedStatus === status.id ? 'bold' : 'normal'
                                            }}
                                        >
                                            {status.label}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                            <Col md={4} className="ps-3 mb-1">
                                <InputGroup className="shadow-sm border-0">
                                    <Input
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-control-sm"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <DataTable
                            columns={columns}
                            data={data}
                            progressPending={loading}
                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            sortServer
                            onSort={handleSort}
                            highlightOnHover
                            customStyles={customStyles}
                            noDataComponent={
                                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                    <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                        <CardBody>
                                            <h6 className="fw-semibold mb-1">No Bystander Found</h6>
                                            <small className="text-muted">No records matching your criteria.</small>
                                        </CardBody>
                                    </Card>
                                </div>
                            }
                        />
                    </CardBody>
                </Card>
            </Container>

            {/* Bystander Detail Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(!viewModal)} size="lg" centered>
                {selectedBystander && (
                    <div style={{
                        background: '#1e6ec8',
                        padding: '1.5rem',
                        position: 'relative',
                        width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={() => setViewModal(false)}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '70px', height: '70px', borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.95)',
                                display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0,
                                padding: '4px', overflow: 'hidden'
                            }}>
                                {selectedBystander.profile_pic ? (
                                    <img src={`${mediaUrl}${selectedBystander.profile_pic}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                ) : (
                                    <User size={30} color="#1e6ec8" />
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.4rem', margin: 0, marginBottom: '0.25rem' }}>
                                    {selectedBystander.name}
                                </h3>
                                <div style={{ display: 'flex', gap: '0.75rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span>{selectedBystander.email || 'No Email'}</span>
                                    <span>•</span>
                                    <span style={{ fontWeight: 600 }}>{selectedBystander.phone_number}</span>
                                </div>
                            </div>
                            <div className="ms-auto pe-4">
                                <div className="d-flex gap-2 align-items-center">
                                    {selectedBystander.status !== 'Approved' && (
                                        <Button color="success" size="sm" className="px-3" onClick={() => handleStatusUpdate(selectedBystander.id, 'Approved')}>Approve</Button>
                                    )}
                                    {selectedBystander.status !== 'Reject' && (
                                        <Button color="danger" size="sm" className="px-3" onClick={() => handleStatusUpdate(selectedBystander.id, 'Reject')}>Reject</Button>
                                    )}
                                    <Badge color={selectedBystander.status === 'Approved' ? 'success' : 'danger'} pill style={{ padding: '8px 16px', fontSize: '0.8rem' }}>
                                        {selectedBystander.status}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal-body p-0">
                    <div className="premium-modal-tabs">
                        <Nav tabs>
                            <NavItem>
                                <NavLink className={activeTab === '1' ? 'active' : ''} onClick={() => setActiveTab('1')} style={{ cursor: 'pointer' }}>
                                    <User size={14} /> Information
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink className={activeTab === '2' ? 'active' : ''} onClick={() => setActiveTab('2')} style={{ cursor: 'pointer' }}>
                                    <FileText size={14} /> Documents
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink className={activeTab === '3' ? 'active' : ''} onClick={() => setActiveTab('3')} style={{ cursor: 'pointer' }}>
                                    <MapPin size={14} /> Location & Tracking
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </div>

                    <div className="p-4 bg-light">
                        {selectedBystander && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <Row className="g-4">
                                            <Col md={6}>
                                                <label className="text-muted small fw-bold text-uppercase">Gender</label>
                                                <div className="fw-semibold fs-6 text-dark">{selectedBystander.gender}</div>
                                            </Col>
                                            <Col md={6}>
                                                <label className="text-muted small fw-bold text-uppercase">Date of Birth</label>
                                                <div className="fw-semibold fs-6 text-dark">{selectedBystander.dob}</div>
                                            </Col>
                                            <Col md={6}>
                                                <label className="text-muted small fw-bold text-uppercase">Phone Number</label>
                                                <div className="fw-semibold fs-6 text-dark">{selectedBystander.phone_number}</div>
                                            </Col>
                                            <Col md={6}>
                                                <label className="text-muted small fw-bold text-uppercase">Email</label>
                                                <div className="fw-semibold fs-6 text-teal" style={{ color: '#0d9488' }}>{selectedBystander.email || 'No Email'}</div>
                                            </Col>
                                            <Col md={12}>
                                                <label className="text-muted small fw-bold text-uppercase">Aadhaar Number</label>
                                                <div className="fw-semibold fs-6 text-dark">{selectedBystander.aadhaar_number}</div>
                                            </Col>
                                            <Col md={6}>
                                                <label className="text-muted small fw-bold text-uppercase">Education</label>
                                                <div className="fw-semibold fs-6 text-dark">{selectedBystander.education}</div>
                                            </Col>
                                            <Col md={6}>
                                                <label className="text-muted small fw-bold text-uppercase">Experience (Years)</label>
                                                <div className="fw-semibold fs-6 text-dark">{selectedBystander.experience_years}</div>
                                            </Col>
                                            <Col md={12}>
                                                <label className="text-muted small fw-bold text-uppercase">Address</label>
                                                <div className="p-3 bg-light rounded mt-1 text-dark" style={{ lineHeight: '1.6' }}>{selectedBystander.address}</div>
                                            </Col>
                                        </Row>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <Row className="g-4">
                                        <Col md={6}>
                                            <div className="bg-white p-3 rounded shadow-sm text-center">
                                                <label className="text-muted small fw-bold text-uppercase d-block mb-2">KYC Document</label>
                                                {isPdf(selectedBystander.kyc_document) ? (
                                                    <a href={`${mediaUrl}${selectedBystander.kyc_document}`} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm mt-2">
                                                        <FileText size={14} /> View PDF
                                                    </a>
                                                ) : (
                                                    <img src={`${mediaUrl}${selectedBystander.kyc_document}`} alt="KYC" className="img-fluid rounded border" style={{ maxHeight: '150px' }} />
                                                )}
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="bg-white p-3 rounded shadow-sm text-center">
                                                <label className="text-muted small fw-bold text-uppercase d-block mb-2">Police Verification</label>
                                                {isPdf(selectedBystander.police_verification) ? (
                                                    <a href={`${mediaUrl}${selectedBystander.police_verification}`} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm mt-2">
                                                        <FileText size={14} /> View PDF
                                                    </a>
                                                ) : (
                                                    <img src={`${mediaUrl}${selectedBystander.police_verification}`} alt="PV" className="img-fluid rounded border" style={{ maxHeight: '150px' }} />
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </TabPane>

                                <TabPane tabId="3">
                                    <Row className="g-4">
                                        <Col md={(selectedBystander.is_online === 1 || selectedBystander.is_online === 2) ? 6 : 12}>
                                            <div className="bg-white p-3 rounded shadow-sm">
                                                <label className="text-muted small fw-bold text-uppercase d-block mb-2 text-center">Home Location</label>
                                                {selectedBystander.homelat && selectedBystander.homelng ? (
                                                    <StaticMapDisplay lat={selectedBystander.homelat} lng={selectedBystander.homelng} label="Home" />
                                                ) : (
                                                    <div className="text-muted text-center py-4">No home location set.</div>
                                                )}
                                            </div>
                                        </Col>
                                        {(selectedBystander.is_online === 1 || selectedBystander.is_online === 2) && (
                                            <Col md={6}>
                                                <div className="bg-white p-3 rounded shadow-sm">
                                                    <label className="text-primary small fw-bold text-uppercase d-block mb-2 text-center">Current Location</label>
                                                    {selectedBystander.current_lat && selectedBystander.current_lng ? (
                                                        <StaticMapDisplay lat={selectedBystander.current_lat} lng={selectedBystander.current_lng} label="Current Location" />
                                                    ) : (
                                                        <div className="text-muted text-center py-4">Current location not found.</div>
                                                    )}
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>
            <ToastContainer />
        </div>
    );
}
